<script lang="ts">
	import { GameService } from '$lib/service/GameService';
	import {
		fetchRelayData,
		getRevealTime,
		getSeason,
		getUserBalls,
		requestReveal
	} from '$lib/service/LuckyballService';
	import { activeGameWallet, keycloak } from '$lib/stores/userstore';
	import type { KeycloakAuth, RelayData, Season, UserBalls, Wallet } from '$lib/types';
	import axios from 'axios';
	import { createEventDispatcher } from 'svelte';
	import Button from '../button/Button.svelte';
	import { CloseIcon } from '../icons';
	import DisplayIcon from '../icons/DisplayIcon.svelte';
	import Overlay from '../loading/Overlay.svelte';
	import TabBar from '../navigation/TabBar.svelte';
	import TabItem from '../navigation/TabItem.svelte';
	import { notifications } from '../toast/notifications';
	import FontSpan from '../typography/FontSpan.svelte';
	import ConfirmModel from './ConfirmModel.svelte';
	import WinBallCard from './WinBallCard.svelte';

	const dispatchClose = createEventDispatcher<{ closed: boolean }>();

	export let open = false;

	function toggle() {
		open = !open;
		if (!open) {
			dispatchClose('closed', true);
		}
	}

	function handleKeydown(event: KeyboardEvent) {
		if (event.key === 'Escape') {
			event.preventDefault();
			event.stopPropagation();
			open = false;
			dispatchClose('closed', true);
		}
	}

	async function loadGameWalletBalance(
		_keycloak: KeycloakAuth | undefined,
		_currentSeasonNumber: number,
		_defaultSeasonNumber: number
	) {
		if (_keycloak) {
			const service = new GameService(_keycloak);
			if (_currentSeasonNumber === -1 && _defaultSeasonNumber > 0) {
				return service.loadLuckybox(_defaultSeasonNumber);
			} else if (_currentSeasonNumber > 0) {
				return service.loadLuckybox(_currentSeasonNumber);
			}
		}
	}
	$: luckyboxPromise = loadGameWalletBalance($keycloak, currentSeasonNumber, defaultSeasonNumber);

	let currentSeasonNumber = -1;
	let defaultSeasonNumber = -1;
	let season: Season | undefined;
	let seasonLoading = false;
	async function loadSeason(_currentSeasonNumber: number) {
		try {
			seasonLoading = true;
			season = await getSeason(_currentSeasonNumber);
			if (_currentSeasonNumber === -1) {
				defaultSeasonNumber = season.seasonId;
			}
		} catch (error) {
			console.log('loadSeason: ', error);
		} finally {
			seasonLoading = false;
		}
	}
	$: loadSeason(currentSeasonNumber);
	function loadPreviousSeason() {
		if (currentSeasonNumber === -1 && defaultSeasonNumber > 0) {
			currentSeasonNumber = defaultSeasonNumber - 1;
		} else if (currentSeasonNumber - 1 > 0) {
			currentSeasonNumber = currentSeasonNumber - 1;
		}
	}
	function loadNextSeason() {
		if (defaultSeasonNumber >= currentSeasonNumber + 1 && currentSeasonNumber !== -1) {
			currentSeasonNumber = currentSeasonNumber + 1;
		}
	}

	let userBalls: UserBalls | undefined = undefined;
	let codesToReveal = false;
	async function loadUserBalls(
		_activeGameWallet: Wallet | undefined,
		_currentSeasonNumber: number
	) {
		try {
			if (_activeGameWallet) {
				userBalls = await getUserBalls(_activeGameWallet.address, _currentSeasonNumber);
				console.log('userBalls', userBalls);
				if (typeof userBalls !== 'undefined') {
					userBalls.ballList.sort((a, b) => b[0] - a[0]);
					for (const ball of userBalls.ballList) {
						if (ball[1] === -1) {
							codesToReveal = true;
							break;
						}
					}
				}
			}
		} catch (error) {
			console.log('loadUserBalls: ', error);
		}
	}
	$: loadUserBalls($activeGameWallet, currentSeasonNumber);

	let relayData: RelayData | undefined;
	async function loadRelayData(_activeGameWallet: Wallet | undefined) {
		try {
			if (_activeGameWallet) {
				relayData = await fetchRelayData(_activeGameWallet.address);
			}
		} catch (error) {
			console.log('loadRelayData: ', error);
		}
	}
	$: loadRelayData($activeGameWallet);

	let showConfimDialog = false;
	let requestCodesProcessing = false;
	async function requestCodesHandler() {
		if (relayData && $activeGameWallet) {
			requestCodesProcessing = true;
			try {
				await requestReveal($activeGameWallet.address, relayData);
				await new Promise((resolve) => setTimeout(resolve, 10000));
				await loadUserBalls($activeGameWallet, currentSeasonNumber);
			} catch (error) {
				console.log('requestCodesHandler: ', error);
				if (axios.isAxiosError(error) && error?.response?.status === 400) {
					notifications.warning(error?.response?.data?.err, 3000);
				}
			} finally {
				requestCodesProcessing = false;
			}
		}
	}

	function getTimeRemaining(endTime: Date) {
		if (!!endTime) {
			const total = endTime.getTime() - new Date().getTime();
			if (total < 1) {
				return {
					total: 0,
					hours: 0,
					minutes: 0,
					seconds: 0
				};
			}
			const seconds = Math.floor((total / 1000) % 60);
			const minutes = Math.floor((total / 1000 / 60) % 60);
			const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
			return {
				total,
				hours,
				minutes,
				seconds
			};
		}

		return {
			total: 0,
			hours: 0,
			minutes: 0,
			seconds: 0
		};
	}

	type Ticker = ReturnType<typeof getTimeRemaining>;
	let ticker: Ticker | undefined;
	async function initializeClock(_showTimer: boolean) {
		const nextRevealTime = await getRevealTime();
		if (codesToReveal) {
			const timeinterval = setInterval(() => {
				const addBufferTime = new Date(nextRevealTime.toString);
				addBufferTime.setMinutes(addBufferTime.getMinutes() + 1);
				ticker = getTimeRemaining(addBufferTime);
				if (ticker.total <= 0) {
					clearInterval(timeinterval);
				}
			}, 1000);
		}
		return undefined;
	}

	$: initializeClock(codesToReveal);

	function padNumberWithZero(number: number) {
		return number.toString().padStart(2, '0');
	}

	function rankValue(_userBalls: UserBalls | undefined, rank: number) {
		if (typeof userBalls !== 'undefined' && userBalls.matchSum && userBalls.matchSum.length > 5) {
			return userBalls.matchSum[rank].toString();
		} else {
			return '0';
		}
	}

	let selectedTabIndex = 0;
	function handleTabChange(e: CustomEvent<number>) {
		selectedTabIndex = e.detail;
	}
</script>

<svelte:window on:keydown={handleKeydown} />

<ConfirmModel
	bind:open={showConfimDialog}
	on:confirm={(e) => {
		if (e.detail) {
			requestCodesHandler();
		}
	}}
/>
<Overlay show={requestCodesProcessing || seasonLoading} />
<div
	class="modal fixed w-full h-full top-0 left-0 flex items-center justify-center z-40"
	class:opacity-0={!open}
	class:pointer-events-none={!open}
>
	<div class="modal-overlay absolute w-full h-full" />

	<div class="modal-container z-50">
		<div
			role="button"
			tabindex="0"
			on:click={toggle}
			on:keypress={handleKeydown}
			class="modal-close absolute top-0 right-0 cursor-pointer flex flex-col items-center mt-4 mr-4 text-white text-sm z-50"
		>
			<DisplayIcon size={18} icon={CloseIcon} css="fill-current text-white" />
			<span class="text-sm">(Esc)</span>
		</div>
		<div class="modal-content relative text-left">
			<div class="modal-header">
				<div class="model-title-container">
					<div
						role="button"
						tabindex="0"
						on:click={loadPreviousSeason}
						on:keypress
						class="cursor-pointer flex self-stretch items-center"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
						>
							<path
								d="M5.63819 13.2275L15.0113 19.3076C15.2118 19.433 15.4391 19.4993 15.6706 19.5C15.9021 19.5007 16.1297 19.4357 16.3308 19.3116C16.5319 19.1875 16.6995 19.0085 16.8169 18.7924C16.9343 18.5763 16.9975 18.3308 17 18.0801L17 5.9199C16.9975 5.66923 16.9343 5.42365 16.8169 5.20759C16.6995 4.99154 16.5319 4.81253 16.3308 4.68839C16.1297 4.56425 15.9021 4.4993 15.6706 4.5C15.4391 4.50071 15.2118 4.56704 15.0113 4.69239L5.63819 10.7725C5.44337 10.9005 5.2823 11.0806 5.17051 11.2957C5.05873 11.5107 5 11.7533 5 12C5 12.2467 5.05873 12.4893 5.17051 12.7043C5.2823 12.9194 5.44337 13.0995 5.63819 13.2275Z"
								fill="white"
							/>
						</svg>
					</div>
					<div class="flex">
						<FontSpan varient="body-B-03" class="text-white"
							>Season {`${season?.seasonId || ''}`.padStart(2, '0')} Lucky Ball</FontSpan
						>
					</div>
					<div
						role="button"
						tabindex="0"
						on:click={loadNextSeason}
						on:keypress
						class="cursor-pointer flex self-stretch items-center"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
						>
							<path
								d="M18.3618 10.7725L8.98865 4.69239C8.78818 4.56704 8.56089 4.50071 8.3294 4.50001C8.09791 4.4993 7.87028 4.56425 7.66917 4.68839C7.46805 4.81253 7.30046 4.99154 7.18306 5.20759C7.06566 5.42365 7.00255 5.66923 7 5.9199L7 18.0801C7.00254 18.3308 7.06566 18.5763 7.18306 18.7924C7.30045 19.0085 7.46805 19.1875 7.66916 19.3116C7.87028 19.4357 8.09791 19.5007 8.3294 19.5C8.56089 19.4993 8.78818 19.433 8.98865 19.3076L18.3618 13.2275C18.5566 13.0995 18.7177 12.9194 18.8295 12.7043C18.9413 12.4893 19 12.2467 19 12C19 11.7533 18.9413 11.5107 18.8295 11.2957C18.7177 11.0806 18.5566 10.9005 18.3618 10.7725Z"
								fill="white"
							/>
						</svg>
					</div>
				</div>
				<div
					role="button"
					tabindex="0"
					on:keypress
					on:click={toggle}
					class="close-button cursor-pointer"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="28"
						height="28"
						viewBox="0 0 28 28"
						fill="none"
					>
						<path
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M21.8249 6.17505C22.2805 6.63066 22.2805 7.36936 21.8249 7.82497L7.82488 21.825C7.36926 22.2806 6.63057 22.2806 6.17496 21.825C5.71935 21.3694 5.71935 20.6307 6.17496 20.1751L20.175 6.17505C20.6306 5.71944 21.3693 5.71944 21.8249 6.17505Z"
							fill="#55DBD7"
						/>
						<path
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M6.17496 6.17505C6.63057 5.71944 7.36926 5.71944 7.82488 6.17505L21.8249 20.1751C22.2805 20.6307 22.2805 21.3694 21.8249 21.825C21.3693 22.2806 20.6306 22.2806 20.175 21.825L6.17496 7.82497C5.71935 7.36936 5.71935 6.63066 6.17496 6.17505Z"
							fill="#55DBD7"
						/>
					</svg>
				</div>
			</div>
			<!--Body-->
			<div class="flex flex-col modal-body">
				<TabBar>
					<TabItem
						on:selectedIndex={handleTabChange}
						index={0}
						active={selectedTabIndex === 0}
						text="LUCKY BALL"
					/>
					<TabItem
						on:selectedIndex={handleTabChange}
						index={1}
						active={selectedTabIndex === 1}
						text="REWARD"
					/>
				</TabBar>
				{#if selectedTabIndex === 0}
					<div class="flex flex-row gap-8">
						<div class="left-content">
							<FontSpan varient="body-EB-015" class="text-gray-40 self-center">
								GENERATING LUCKY BALL
							</FontSpan>
							<div class="p-3 gap-10 flex flex-row w-full justify-center">
								<div class="icon icon-match-ball-reveal74" />
								<div class="flex flex-col justify-center items-center">
									<FontSpan varient="body-R-06" class="text-success-300">
										Number of lucky balls
									</FontSpan>
									<FontSpan varient="body-R-06" class="text-success-300">
										available for code request
									</FontSpan>
									<FontSpan varient="body-R-02" class="text-white">
										{typeof userBalls === 'undefined' ? '-' : userBalls.unrevealCount.toString()}
									</FontSpan>
								</div>
							</div>

							<div class="flex bg-gray-200 rounded-lg p-3">
								<FontSpan varient="body-R-06" class="text-gray-40 text-justify">
									Lucky Ball obtained are initially added to the Pending Ball section and move to
									the Received Ball section every 2 hours. When you press the Requst Codes botton
									while having Recevied Ball. a list will be generated after a certain amount of
									time. allowing you to open and check whether you have won.
								</FontSpan>
							</div>
							<div class="flex flex-col items-center justify-center w-full">
								{#if ticker && codesToReveal && typeof userBalls !== 'undefined' && userBalls.unrevealCount === 0}
									<Button
										enable={ticker.hours === 0 && ticker.minutes === 0 && ticker.seconds === 0}
										on:click={() => {
											loadUserBalls($activeGameWallet, currentSeasonNumber);
											codesToReveal = false;
										}}
										type="secondary"
										class="w-[250px] py-2"
									>
										<FontSpan varient="body-EB-04" class="text-white">Reveal All</FontSpan>
									</Button>
								{:else}
									<Button
										enable={typeof userBalls !== 'undefined' &&
											userBalls.unrevealCount > 0 &&
											season &&
											season.isActive}
										on:click={() => (showConfimDialog = true)}
										type="secondary"
										class="w-[250px] py-2"
									>
										<FontSpan
											varient="body-EB-04"
											class={typeof userBalls !== 'undefined' && userBalls.unrevealCount > 0
												? 'text-white'
												: 'text-gray-80'}
										>
											Request codes
										</FontSpan>
									</Button>
								{/if}
								{#if ticker && codesToReveal && (ticker.hours > 0 || ticker.minutes > 0 || ticker.seconds > 0)}
									<FontSpan varient="body-EB-04" class="text-gray-80 mt-2">
										{padNumberWithZero(ticker.hours)}:{padNumberWithZero(
											ticker.minutes
										)}:{padNumberWithZero(ticker.seconds)} to reveal lucky balls
									</FontSpan>
								{/if}
							</div>
						</div>
						{#if typeof userBalls !== 'undefined'}
							<div class="right-content">
								<div class="grid grid-cols-1 gap-y-4 gap-x-2 w-full">
									{#each userBalls.ballList as ball}
										<WinBallCard code={ball[1]} id={ball[0]} match={ball[2]} />
									{/each}
								</div>
							</div>
						{:else}
							<div class="right-content">
								<div class="flex flex-col items-center justify-center w-full h-full">
									<div class="flex icon icon-no-lucky-balls74" />
									<FontSpan varient="body-R-06" class="text-success-300 mt-2"
										>No lucky ball</FontSpan
									>
								</div>
							</div>
						{/if}
					</div>
				{/if}
				{#if selectedTabIndex === 1}
					<div class="flex flex-row gap-8">
						<div class="flex flex-row flex-grow basis-0">
							<div class="flex flex-col flex-grow gap-4">
								<div class="flex flex-grow flex-row h-14 items-center row">
									<div class="flex flex-grow basis-0">
										<FontSpan varient="body-R-06" class="flex flex-grow text-gray-40 text-center"
											>Grade</FontSpan
										>
									</div>
									<div class="flex flex-grow basis-0">
										<FontSpan varient="body-R-06" class="flex flex-grow text-gray-40 text-center"
											>Winning Code</FontSpan
										>
									</div>
									<div class="flex flex-grow basis-0">
										<FontSpan varient="body-R-06" class="flex flex-grow text-gray-40 text-center"
											>Gift</FontSpan
										>
									</div>
									<div class="flex flex-grow basis-0">
										<FontSpan varient="body-R-06" class="flex flex-grow text-gray-40 text-center"
											>My wins</FontSpan
										>
									</div>
									<div class="flex flex-grow basis-0">
										<FontSpan varient="body-R-06" class="flex flex-grow text-gray-40 text-center"
											>All user wins</FontSpan
										>
									</div>
								</div>
								{#each Array.from( { length: 6 }, (value, index) => ({ rankIndex: index + 1, textIndex: 6 - index }) ) as item}
									<div class="flex flex-row flex-grow h-14 items-center row">
										<div class="flex flex-grow basis-0">
											<div class="m-auto icon icon-grade-{item.rankIndex}-34" />
										</div>
										<div class="flex flex-grow basis-0">
											<FontSpan varient="body-EB-04" class="flex flex-grow text-white text-center"
												>{season?.winningCode}</FontSpan
											>
										</div>
										<div class="flex flex-grow basis-0">
											<div class="m-auto icon icon-gift-{item.rankIndex}-48" />
										</div>
										<div class="flex flex-grow basis-0">
											<FontSpan varient="body-EB-04" class="flex flex-grow text-white text-center"
												>x {rankValue(userBalls, item.textIndex)}</FontSpan
											>
										</div>
										<div class="flex flex-grow basis-0">
											<FontSpan varient="body-EB-04" class="flex flex-grow text-white text-center"
												>99999</FontSpan
											>
										</div>
									</div>
								{/each}
							</div>
						</div>
						<div class="flex flex-grow flex-col items-center basis-0 gap-4">
							<FontSpan varient="body-EB-015" class="text-gray-40">LUCKY BOX</FontSpan>
							<div class="flex w-full flex-row gap-6 justify-center items-center">
								<div class="flex">
									<div class="flex icon icon-luckybox-100" />
								</div>
								<div class="flex flex-col w-full items-center">
									<FontSpan varient="body-B-06" class="text-gray-40">Prize Cring</FontSpan>
									<div
										class="flex flex-row flex-grow w-full h-11 bg-gray-200 rounded-md items-center px-3 gap-2"
									>
										<div class="flex flex-grow h-5 basis-1">
											<div class="flex icon icon-cring20" />
										</div>
										<FontSpan varient="body-EB-02" class="flex flex-grow basis-1 text-white">
											{#await luckyboxPromise}
												0
											{:then value}
												{value ? value.luckybox : '0'}
											{:catch error}
												0
											{/await}
										</FontSpan>
									</div>
								</div>
							</div>
							<div class="flex w-full flex-row gap-6 justify-center items-center">
								<div class="flex flex-row gap-2 min-w-[100px] items-center">
									<div class="flex">
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width="24"
											height="24"
											viewBox="0 0 24 24"
											fill="none"
										>
											<path
												d="M5.25 3C4.65326 3 4.08097 3.23705 3.65901 3.65901C3.23705 4.08097 3 4.65326 3 5.25V7.0395C3 7.8195 3.405 8.5425 4.0665 8.952L9.8655 12.537C8.98346 13.0122 8.28524 13.7683 7.88151 14.6853C7.47778 15.6022 7.39162 16.6277 7.63669 17.5992C7.88176 18.5707 8.44403 19.4327 9.23441 20.0484C10.0248 20.6642 10.9981 20.9986 12 20.9986C13.0019 20.9986 13.9752 20.6642 14.7656 20.0484C15.556 19.4327 16.1182 18.5707 16.3633 17.5992C16.6084 16.6277 16.5222 15.6022 16.1185 14.6853C15.7148 13.7683 15.0165 13.0122 14.1345 12.537L19.9335 8.952C20.2594 8.75042 20.5285 8.46894 20.7151 8.13424C20.9018 7.79955 20.9998 7.42272 21 7.0395V5.25C21 4.65326 20.7629 4.08097 20.341 3.65901C19.919 3.23705 19.3467 3 18.75 3H5.25ZM4.5 5.25C4.5 5.05109 4.57902 4.86032 4.71967 4.71967C4.86032 4.57902 5.05109 4.5 5.25 4.5H7.5V9.312L4.8555 7.677C4.74686 7.60981 4.65718 7.51598 4.59496 7.40442C4.53275 7.29285 4.50006 7.16724 4.5 7.0395V5.25ZM9 10.239V4.5H15V10.239L12.3945 11.85C12.276 11.9233 12.1394 11.9621 12 11.9621C11.8606 11.9621 11.724 11.9233 11.6055 11.85L9 10.239ZM16.5 9.312V4.5H18.75C18.9489 4.5 19.1397 4.57902 19.2803 4.71967C19.421 4.86032 19.5 5.05109 19.5 5.25V7.0395C19.4999 7.16724 19.4673 7.29285 19.405 7.40442C19.3428 7.51598 19.2531 7.60981 19.1445 7.677L16.5 9.312ZM9 16.5C9 15.7044 9.31607 14.9413 9.87868 14.3787C10.4413 13.8161 11.2044 13.5 12 13.5C12.7956 13.5 13.5587 13.8161 14.1213 14.3787C14.6839 14.9413 15 15.7044 15 16.5C15 17.2956 14.6839 18.0587 14.1213 18.6213C13.5587 19.1839 12.7956 19.5 12 19.5C11.2044 19.5 10.4413 19.1839 9.87868 18.6213C9.31607 18.0587 9 17.2956 9 16.5Z"
												fill="#C8C8C8"
											/>
										</svg>
									</div>
									<FontSpan varient="body-B-06" class="flex flex-grow text-gray-40">Winner</FontSpan
									>
								</div>
								<div
									class="flex flex-row flex-grow w-full h-11 bg-gray-200 rounded-md items-center justify-center"
								>
									<FontSpan varient="body-EB-02" class="text-white">
										{season
											? season.winningBallId === 0
												? 'Winner TBD'
												: season.winningBallId
											: '-'}
									</FontSpan>
								</div>
							</div>
							<div class="flex w-full flex-row mb-16">
								<FontSpan varient="body-R-06" class="text-gray-40">
									Lucky Box represents accrued tank repair, entry fee of all users who attend in the
									season and will be awarded to 1 user via ball id lottery after the season ends.
									Ends without a winner if the game account for the winning ball id not exist.
								</FontSpan>
							</div>

							<div class="flex w-full flex-row gap-6 rounded-md bg-gray-200 p-3">
								<div class="flex">
									<div class="flex icon icon-LuckyBall-100" />
								</div>
								<div class="flex flex-col flex-grow items-center justify-center gap-2">
									<FontSpan varient="body-B-06" class="flex text-gray-40">Total My Ball</FontSpan>
									<div class="flex w-full h-11 bg-bg rounded-md items-center justify-center">
										<FontSpan varient="body-EB-02" class=" text-white">
											{userBalls ? userBalls.total : 0}
										</FontSpan>
									</div>
								</div>
							</div>
						</div>
					</div>
				{/if}
			</div>
		</div>
	</div>
</div>

<style>
	.row {
		border-radius: 6px;
		background: var(--gray-200, #282828);
		box-shadow: 0px 0px 16px 0px rgba(0, 0, 0, 0.45);
	}

	.type-container {
		display: flex;
		height: 122px;
		padding: 5px 5px 0px 5px;
		align-items: flex-start;
		gap: 5px;
		flex-shrink: 0;
		align-self: stretch;
	}
	.right-content {
		overflow: auto;
		height: 515px;

		display: flex;
		padding-right: 5px;
		align-items: flex-start;
		gap: 10px;
		flex: 1 0 0;
		align-self: stretch;
	}
	.left-content {
		display: flex;
		height: 405px;
		padding: 10px;
		flex-direction: column;
		align-items: flex-start;
		gap: 10px;
		flex: 1 0 0;
	}
	.modal-body {
		width: 888px;
		padding: 24px 24px 24px 24px;
		gap: 10px;
	}
	.close-button {
		height: 100%;
		display: flex;
		justify-content: center;
		align-items: center;

		border-radius: 6px;
		box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
	}
	.model-title-container {
		display: flex;
		width: 801px;
		height: 39px;
		padding: 0px 32px 2px 0px;
		justify-content: center;
		align-items: flex-start;
		gap: 12px;
	}
	.modal-header {
		display: flex;
		height: 39px;
		padding: 0px 16px;
		justify-content: center;
		align-items: flex-start;
		gap: 13px;
		flex-shrink: 0;
		align-self: stretch;
	}
	.modal-container {
		display: flex;
		padding: 12px;
		flex-direction: column;
		align-items: center;
		gap: 40px;

		border-radius: 30px;
		border: 1px solid var(--popup-border, #2d2d2d);
		background: var(--bg, #151516);

		/* popup_shadow */
		box-shadow: 0px 0px 20px 0px rgba(189, 189, 189, 0.2);
	}
	.modal-overlay {
		background: rgba(0, 0, 0, 0.8);
		backdrop-filter: blur(8px);
	}

	.modal {
		transition: opacity 0.25s ease;
	}
</style>
